import { useState } from "react"
import { Button, Checkbox, Heading, Select, Text } from "@smooth/design-system-components"
import { formatDateTime } from "../../components/shared"
import { terminalOptionLabel } from "../types"
import type { ImportTimeline, Terminals } from "../types"
import { TextButton } from "./parts"

/** 時点を切り取るときの基準日時は、選んだ発言の1秒後（発言そのものは会話に含める） */
export const CUT_AFTER_MS = 1000

export type TimelinePoint = { at: string; expectedTerminal: string }

const STATUS_LABELS: Record<string, string> = {
  succeeded: "生成成功",
  failed: "生成失敗",
  human_review: "人手対応",
}

type Props = {
  timeline: ImportTimeline
  terminals: Terminals
  busy: boolean
  /** 選んだ発言の直後で切って、作成フォームに入れる（保存はしない） */
  onCut: (at: string) => void
  /** 選んだ時点ごとに、期待する対応を付けてケースを保存する */
  onCreatePoints: (points: TimelinePoint[]) => void
  /** 本番の生成リクエストの入力で読み込む（保存はしない） */
  onGenerationRequest: (id: number) => void
}

/**
 * お客様の会話のタイムライン。発言ごとに「この時点で切る」か、複数の時点に期待する対応を付けてまとめて作る。
 * 本番で実際に AI ドラフトを生成したときの入力（生成リクエスト）からも読み込める。
 */
export function ImportTimelinePicker({
  timeline,
  terminals,
  busy,
  onCut,
  onCreatePoints,
  onGenerationRequest,
}: Props) {
  // 選んだ発言（インデックス）→ 期待する対応（未選択は ""）
  const [chosen, setChosen] = useState<Record<number, string>>({})
  const chosenIndexes = Object.keys(chosen)
    .map(Number)
    .sort((a, b) => a - b)
  const ready = chosenIndexes.length > 0 && chosenIndexes.every((index) => chosen[index])
  const terminalOptions = Object.entries(terminals).map(([id, label]) => ({
    value: id,
    label: terminalOptionLabel(id, label),
  }))

  const toggle = (index: number, checked: boolean) =>
    setChosen((current) => {
      const next = { ...current }
      if (checked) next[index] = ""
      else delete next[index]
      return next
    })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Heading level={3}>会話から時点を選ぶ（督促ケース#{timeline.sourceDunningCaseId}）</Heading>
        <Text size="sm">
          発言の直後で切ると、その時点の状態（会話・約束・入金・交渉記録・メモ）でケースを作ります。お客様の発言で切ると、その発言に返信する場面になります。複数の時点にチェックを付けて期待する対応を選ぶと、時点ごとにケースを保存します。
        </Text>
      </div>
      {timeline.messages.length === 0 ? (
        <Text size="sm">直近の会話がありません。</Text>
      ) : (
        <ol
          aria-label="会話のタイムライン"
          className="flex flex-col border-t border-border-default"
        >
          {timeline.messages.map((message, index) => {
            const checked = index in chosen
            return (
              <li
                key={`${message.at}-${index}`}
                className={`flex flex-col gap-2 border-b border-border-default py-3 ${
                  message.direction === "inbound" ? "" : "pl-6"
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Checkbox
                    label={`${formatDateTime(message.at)} ${
                      message.direction === "inbound" ? "お客様" : "当社"
                    }`}
                    checked={checked}
                    disabled={busy}
                    onChange={(e) => toggle(index, e.target.checked)}
                  />
                  <TextButton
                    disabled={busy}
                    onClick={() => onCut(message.at)}
                    aria-label={`${formatDateTime(message.at)} の発言の直後で切る`}
                  >
                    この時点で切る
                  </TextButton>
                </div>
                <Text size="sm">{message.body}</Text>
                {checked && (
                  <div className="w-full max-w-[360px]">
                    <Select
                      aria-label={`${formatDateTime(message.at)} の時点で期待する対応`}
                      value={chosen[index]}
                      disabled={busy}
                      placeholder="この時点で期待する対応"
                      onChange={(e) =>
                        setChosen((current) => ({ ...current, [index]: e.target.value }))
                      }
                      options={terminalOptions}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          disabled={busy || !ready}
          onClick={() =>
            onCreatePoints(
              chosenIndexes.map((index) => ({
                at: timeline.messages[index].at,
                expectedTerminal: chosen[index],
              }))
            )
          }
        >
          {chosenIndexes.length > 0
            ? `選んだ ${chosenIndexes.length} 時点のケースを作る`
            : "時点を選んでケースを作る"}
        </Button>
        {chosenIndexes.length > 0 && !ready && (
          <Text size="sm">選んだ時点すべてに期待する対応を選んでください。</Text>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-border-default pt-4">
        <Heading level={3}>本番で生成したときの入力から読み込む</Heading>
        <Text size="sm">
          実際に AI ドラフトを生成したときに AI
          へ渡した入力をそのまま使います（その時点の状態を組み立て直すより正確です）。
        </Text>
        {timeline.generationRequests.length === 0 ? (
          <Text size="sm">このお客様の生成の記録はありません。</Text>
        ) : (
          <ul aria-label="生成の記録" className="flex flex-col">
            {timeline.generationRequests.map((request) => (
              <li
                key={request.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-default py-2"
              >
                <Text size="sm">
                  {[
                    `生成#${request.id}`,
                    formatDateTime(request.createdAt),
                    request.promptVersion ? `プロンプト v${request.promptVersion}` : "版不明",
                    STATUS_LABELS[request.status] ?? request.status,
                  ].join("・")}
                </Text>
                <TextButton disabled={busy} onClick={() => onGenerationRequest(request.id)}>
                  この生成の入力で読み込む
                </TextButton>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
